import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { useLang } from "@/i18n/LanguageContext";
import PageHero from "@/components/site/PageHero";
import { Reveal, SectionHead } from "@/components/site/Reveal";

const ChiSonoPage = () => {
  const { t } = useLang();
  useSeo("chisono");
  const bio = t("chisono.bio");

  return (
    <div data-testid="chi-sono-page">
      <PageHero
        testId="chi-sono-hero"
        image="/assets/editorial-tower.webp"
        eyebrow={t("chisono.eyebrow")}
        lines={[t("chisono.titleA"), t("chisono.titleB")]}
      />

      <section className="py-24 lg:py-36" data-testid="chi-sono-story">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5 relative">
            <Reveal>
              <div className="relative max-w-sm">
                <div className="absolute -top-4 -left-4 w-full h-full border border-[#C5A059]/40 pointer-events-none" />
                <img
                  src="/assets/giorgio-portrait.webp"
                  alt={t("chisono.portraitAlt")}
                  className="w-full object-cover photo-frame"
                  style={{ aspectRatio: "3/4.4", objectPosition: "center 20%" }}
                  data-testid="chi-sono-portrait"
                />
                <img
                  src="/assets/giorgio-field.webp"
                  alt={t("chisono.fieldAlt")}
                  loading="lazy"
                  className="absolute -bottom-10 -right-8 lg:-right-16 w-32 lg:w-40 object-cover border-4 border-[#F7F5F0] photo-frame"
                  style={{ aspectRatio: "3/4" }}
                />
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-16 text-[11px] tracking-[0.25em] uppercase text-[#666157]">
                Giorgio Panatero — <span className="text-[#9E7B36]">{t("chisono.role")}</span>
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <SectionHead eyebrow={t("chisono.storyEyebrow")} title={<>{t("chisono.storyTitleA")}<br />{t("chisono.storyTitleB")}</>} />
            <Reveal delay={0.15}>
              <div className="mt-8 space-y-6 text-base leading-relaxed text-[#666157]">
                {bio.map((p, i) => (
                  <p key={i}>
                    {i === 0 ? (
                      <>
                        {t("chisono.bio.0").split("Giorgio Panatero")[0]}
                        <strong className="text-[#1C1B18] font-medium">Giorgio Panatero</strong>
                        {t("chisono.bio.0").split("Giorgio Panatero")[1]}
                      </>
                    ) : i === bio.length - 1 ? (
                      <>
                        {p.split("Echoes From Above")[0]}
                        <Link to="/echoes-from-above" className="text-[#9E7B36] editorial-link" data-testid="chi-sono-echoes-link">Echoes From Above</Link>
                        {p.split("Echoes From Above")[1]}
                      </>
                    ) : p}
                  </p>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <figure className="mt-12 border border-[#C5A059]/40 p-8 lg:p-10 relative" data-testid="chi-sono-quote">
                <span className="font-serif-display text-6xl text-[#C5A059] absolute -top-6 left-6 bg-[#F7F5F0] px-2 leading-none">"</span>
                <blockquote className="font-serif-display italic text-2xl lg:text-3xl text-[#1C1B18] leading-snug text-center">
                  {t("chisono.quote1")}<br />{t("chisono.quote2")}
                </blockquote>
              </figure>
            </Reveal>

            <Reveal delay={0.3}>
              <Link
                to="/contatti"
                data-testid="chi-sono-contact-cta"
                className="mt-12 inline-flex items-center gap-3 bg-[#1C1B18] text-[#F7F5F0] px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#9E7B36] transition-colors duration-500"
              >
                {t("chisono.cta")} <ArrowRight size={13} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ChiSonoPage;
